import { colors } from 'fitify-ui'
import { Typography } from 'fitify-ui-landing/src/components/Typography/Typography'
import type { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'

import { Cta } from '@/components/exercises/Cta/Cta'
import { ExerciseCard } from '@/components/exercises/ExerciseCard/ExerciseCard'
import { Filter } from '@/components/exercises/Exercises/Filter/Filter'
import {
  StyledContainer,
  StyledExerciseList,
  StyledResultCount,
  StyledSectionContainer,
  StyledSubtitle,
} from '@/components/exercises/Exercises/Page.Styled'
import { Pagination } from '@/components/exercises/Exercises/Pagination/Pagination'
import { SearchInput } from '@/components/exercises/Exercises/SearchInput/SearchInput'
import {
  type UseExercisesProps,
  useExercises,
} from '@/components/exercises/Exercises/useExercises'
import Layout from '@/components/layouts/Layout'
import { getAllExercises } from '@/utils/exercises.server'
import { getStaticPropsTranslations } from '@/utils/i18n'

type ExerciseListPageProps = UseExercisesProps

const ExerciseListPage: NextPage<ExerciseListPageProps> = ({ exercises }) => {
  const { t } = useTranslation()

  const {
    equipmentFilter,
    equipmentOptions,
    handleFilterChange,
    muscleFilter,
    muscleOptions,
    page,
    paginatedExercises,
    search,
    setEquipmentFilter,
    setMuscleFilter,
    setPage,
    setSearch,
    totalExercises,
    totalPages,
  } = useExercises({ exercises })

  const title = t('landing_exercises_seo_title')
  const description = t('landing_exercises_seo_description')

  return (
    <Layout seo={{ title, description }}>
      <StyledContainer>
        <StyledSectionContainer>
          <Typography
            $color={colors.Black}
            $variant="Headline/H 32 Bold"
            $variantPhone="Headline/H 28 Bold"
          >
            {t('landing_exercises_library_title')}
          </Typography>
          <StyledSubtitle
            $color={colors.Gray600}
            $variant="Text/Body 16"
            $variantPhone="Text/Body 14"
          >
            {t('landing_exercises_library_subtitle')}
          </StyledSubtitle>
        </StyledSectionContainer>

        <StyledSectionContainer>
          <SearchInput
            placeholder={t('landing_exercises_search_placeholder')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
          />

          <Filter
            label={t('landing_exercises_filter_equipment')}
            filters={equipmentOptions}
            activeFilter={equipmentFilter}
            onFilterChange={handleFilterChange(setEquipmentFilter)}
          />

          <Filter
            label={t('landing_exercises_filter_muscles')}
            filters={muscleOptions}
            activeFilter={muscleFilter}
            onFilterChange={handleFilterChange(setMuscleFilter)}
          />

          <StyledResultCount
            $color={colors.Gray600}
            $variant="Special/Label 14"
            $isHidden={
              equipmentFilter === 'all' &&
              muscleFilter === 'all' &&
              !search.trim()
            }
          >
            {t('landing_exercises_count', { count: totalExercises })}
          </StyledResultCount>

          <StyledExerciseList>
            {paginatedExercises.map((exercise) => (
              <ExerciseCard key={exercise.code} exercise={exercise} />
            ))}
          </StyledExerciseList>
        </StyledSectionContainer>

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => {
            setPage(newPage)
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
        <Cta />
      </StyledContainer>
    </Layout>
  )
}

export default ExerciseListPage

export const getStaticProps: GetStaticProps<ExerciseListPageProps> = async ({
  locale,
}) => {
  const exercises = await getAllExercises()

  return {
    props: {
      exercises,
      ...(await getStaticPropsTranslations(locale || 'en')),
    },
  }
}
